import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/accounting/cash` — cash registers and the cash book.
 *
 * The balance is never edited: it is entered once when the register is
 * created and from then on the documents decide it. Money that appeared
 * without a document is exactly what a cash book prevents.
 */

type Register = {
  registerId: string;
  name: string;
  currency: string;
  openingBalance: string;
  openingOn: string;
  income: string;
  expense: string;
  balance: string;
  documentCount: number;
};

type BookRow = {
  id: string;
  number: string;
  issuedOn: string;
  description: string;
  income: string | null;
  expense: string | null;
  balance: string;
};

const INPUT = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

export const Route = createFileRoute("/_app/accounting/cash")({
  loader: async () => {
    try {
      return await api.get<{ registers: Register[] }>("/api/accounting/cash");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: CashPage,
});

function money(amount: string, currency: string): string {
  return `${Number(amount).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function CashPage() {
  const { registers } = Route.useLoaderData();
  const router = useRouter();
  const [openBook, setOpenBook] = useState<string | null>(null);

  const addRegister = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/cash", body),
    { onSuccess: () => router.invalidate(), errorMessage: () => "Couldn't create the register." },
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-semibold text-2xl">Pokladna</h1>
        <p className="text-muted-foreground text-sm">
          The balance is entered once — at the migration or after a stocktake. From then on the
          documents compute it.
        </p>
      </div>

      {registers.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground text-sm">
            No cash register yet.
          </CardContent>
        </Card>
      ) : (
        registers.map((register) => (
          <Card key={register.registerId}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="text-base">{register.name}</CardTitle>
                <CardDescription>
                  Opening balance {money(register.openingBalance, register.currency)} as of{" "}
                  <LocalDate value={register.openingOn} /> · {register.documentCount} documents
                </CardDescription>
              </div>
              <div className="text-right">
                <div className="font-semibold text-xl">
                  {money(register.balance, register.currency)}
                </div>
                <div className="text-muted-foreground text-xs">
                  in {money(register.income, register.currency)} · out{" "}
                  {money(register.expense, register.currency)}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <NewDocumentForm register={register} onDone={() => router.invalidate()} />
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setOpenBook(openBook === register.registerId ? null : register.registerId)
                }
              >
                {openBook === register.registerId ? "Hide the book" : "Cash book"}
              </Button>
              {openBook === register.registerId && <CashBook register={register} />}
            </CardContent>
          </Card>
        ))
      )}

      <Card>
        <CardHeader>
          <CardTitle>New cash register</CardTitle>
          <CardDescription>
            Enter the opening balance as it really was on that day — everything else is computed
            from it.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 sm:grid-cols-5"
            onSubmit={(event) => {
              event.preventDefault();
              const fd = new FormData(event.currentTarget);
              addRegister.mutate({
                name: String(fd.get("name") ?? "").trim(),
                currency: String(fd.get("currency") ?? "CZK").trim() || "CZK",
                openingBalance: Number(fd.get("openingBalance") ?? 0),
                openingOn: String(fd.get("openingOn") ?? ""),
              });
              event.currentTarget.reset();
            }}
          >
            <div className="sm:col-span-2">
              <label htmlFor="name" className="text-muted-foreground text-xs">
                Name
              </label>
              <input id="name" name="name" required placeholder="Main register" className={INPUT} />
            </div>
            <div>
              <label htmlFor="currency" className="text-muted-foreground text-xs">
                Currency
              </label>
              <input id="currency" name="currency" defaultValue="CZK" className={INPUT} />
            </div>
            <div>
              <label htmlFor="openingBalance" className="text-muted-foreground text-xs">
                Opening balance
              </label>
              <input
                id="openingBalance"
                name="openingBalance"
                type="number"
                step="0.01"
                defaultValue="0"
                className={INPUT}
              />
            </div>
            <div>
              <label htmlFor="openingOn" className="text-muted-foreground text-xs">
                K datu
              </label>
              <input id="openingOn" name="openingOn" type="date" required className={INPUT} />
            </div>
            <div className="sm:col-span-5">
              <Button type="submit" disabled={addRegister.pending}>
                {addRegister.pending ? "Creating…" : "Create register"}
              </Button>
              {addRegister.error && (
                <span className="ml-2 text-destructive text-sm">{addRegister.error}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function NewDocumentForm({ register, onDone }: { register: Register; onDone: () => void }) {
  const issue = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/cash/documents", body),
    { onSuccess: onDone, errorMessage: () => "Couldn't issue the document." },
  );

  return (
    <form
      className="grid gap-3 border-t pt-4 sm:grid-cols-6"
      onSubmit={(event) => {
        event.preventDefault();
        const fd = new FormData(event.currentTarget);
        issue.mutate({
          registerId: register.registerId,
          type: String(fd.get("type")),
          issuedOn: String(fd.get("issuedOn") ?? ""),
          amount: Number(fd.get("amount") ?? 0),
          description: String(fd.get("description") ?? "").trim(),
          partnerName: String(fd.get("partnerName") ?? "").trim() || null,
        });
        event.currentTarget.reset();
      }}
    >
      <div>
        <label htmlFor={`type-${register.registerId}`} className="text-muted-foreground text-xs">
          Typ
        </label>
        <select id={`type-${register.registerId}`} name="type" className={INPUT}>
          <option value="income">Receipt</option>
          <option value="expense">Payment</option>
        </select>
      </div>
      <div>
        <label htmlFor={`date-${register.registerId}`} className="text-muted-foreground text-xs">
          Datum
        </label>
        <input
          id={`date-${register.registerId}`}
          name="issuedOn"
          type="date"
          required
          className={INPUT}
        />
      </div>
      <div>
        <label htmlFor={`amount-${register.registerId}`} className="text-muted-foreground text-xs">
          Amount
        </label>
        <input
          id={`amount-${register.registerId}`}
          name="amount"
          type="number"
          step="0.01"
          min="0.01"
          required
          className={INPUT}
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`desc-${register.registerId}`} className="text-muted-foreground text-xs">
          Purpose
        </label>
        <input
          id={`desc-${register.registerId}`}
          name="description"
          required
          placeholder="Office supplies"
          className={INPUT}
        />
      </div>
      <div className="flex items-end">
        <Button type="submit" className="w-full" disabled={issue.pending}>
          {issue.pending ? "Vystavuji…" : "Vystavit"}
        </Button>
      </div>
      {issue.error && <p className="text-destructive text-sm sm:col-span-6">{issue.error}</p>}
    </form>
  );
}

function CashBook({ register }: { register: Register }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [book, setBook] = useState<{
    year: number;
    opening: string;
    rows: BookRow[];
    years: number[];
  } | null>(null);

  if (book === null || book.year !== year) {
    void api
      .get<{ opening: string; rows: BookRow[]; closing: string; years: number[] }>(
        `/api/accounting/cash/${register.registerId}/book?from=${year}-01-01&to=${year}-12-31`,
      )
      .then((b) => setBook({ year, opening: b.opening, rows: b.rows, years: b.years }))
      .catch(() => setBook({ year, opening: "0", rows: [], years: [year] }));
    return <p className="text-muted-foreground text-sm">Loading…</p>;
  }
  const { opening, rows, years } = book;

  return (
    <div className="space-y-2">
      <div className="flex gap-3 text-sm">
        {years.map((y) => (
          <button
            key={y}
            type="button"
            onClick={() => setYear(y)}
            className={y === year ? "font-semibold" : "text-muted-foreground hover:underline"}
          >
            {y}
          </button>
        ))}
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground">
          <tr>
            <th className="py-1 pr-4 font-medium">Doklad</th>
            <th className="py-1 pr-4 font-medium">Datum</th>
            <th className="py-1 pr-4 font-medium">Purpose</th>
            <th className="py-1 pr-4 text-right font-medium">In</th>
            <th className="py-1 pr-4 text-right font-medium">Out</th>
            <th className="py-1 text-right font-medium">Balance</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t text-muted-foreground">
            <td className="py-1 pr-4" colSpan={5}>
              Opening balance
            </td>
            <td className="py-1 text-right tabular-nums">{money(opening, register.currency)}</td>
          </tr>
          {rows.map((row) => (
            <tr key={row.id} className="border-t">
              <td className="py-1 pr-4 font-mono text-xs">{row.number}</td>
              <td className="py-1 pr-4 text-muted-foreground">
                <LocalDate value={row.issuedOn} />
              </td>
              <td className="py-1 pr-4">{row.description}</td>
              <td className="py-1 pr-4 text-right tabular-nums text-emerald-600">
                {row.income ? money(row.income, register.currency) : ""}
              </td>
              <td className="py-1 pr-4 text-right tabular-nums">
                {row.expense ? money(row.expense, register.currency) : ""}
              </td>
              <td className="py-1 text-right tabular-nums">
                {money(row.balance, register.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
